/**
 * Media kind and effective MIME type for a guest-selected File (issues #278, #397).
 *
 * Some browsers and platforms report a blank `file.type` for MOV, HEIC and HEIF files (for
 * example Chrome on Windows without a registered handler). The API still needs a usable
 * content type: it accepts any known video extension, but an image needs a known image
 * extension AND a content type starting with `image/`. So when the browser type is blank (or the
 * generic `application/octet-stream`) we infer the type from the file extension.
 *
 * The API rejects an upload whose extension is not in its tables (400 "Unsupported media type"),
 * whatever the browser reported, so a file with an unknown extension is rejected here too, before
 * any request: a browser type of `image/svg+xml` or `video/x-unknown` does not make it uploadable.
 *
 * AVIF is deliberately not offered (#397): the photo processor decodes 8-bit AVIF only, so a 10-bit
 * (HDR) file would upload and then never get a thumbnail. A `.avif` is refused up front like any
 * other unsupported type.
 *
 * These tables mirror the server rules and must be kept in sync with them:
 * - VIDEO_MIME_TYPES  <- wedding-functions/lib/videoMimeTypes.js (`videoMimeTypes`)
 * - IMAGE_MIME_TYPES  <- `SUPPORTED_IMAGE_EXTENSIONS` in
 *   wedding-functions/controllers/mediaController.js (`isSupportedOriginalUpload`)
 * src/utils/mediaFileType.contract.test.js fails when they drift. The gallery's video check
 * (PhotoGallery/utils/photoHelpers.js) and download MIME map (PhotoGallery/utils/downloadHelpers.js)
 * are built from these tables; public/service-worker.js cannot import them, so the same test
 * checks its copy.
 */

export const VIDEO_MIME_TYPES = {
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
  webm: 'video/webm',
  m4v: 'video/x-m4v',
  '3gp': 'video/3gpp',
  flv: 'video/x-flv',
  wmv: 'video/x-ms-wmv',
  qt: 'video/quicktime',
  mts: 'video/mp2t',
  m2ts: 'video/mp2t',
  ts: 'video/mp2t',
  vob: 'video/mpeg',
  mpg: 'video/mpeg',
  mpeg: 'video/mpeg',
  ogv: 'video/ogg',
  mxf: 'application/mxf',
};

export const IMAGE_MIME_TYPES = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
  heif: 'image/heif',
  hif: 'image/heif',
};

/**
 * Value for the file picker's `accept` attribute. The `image/*,video/*` wildcards can hide
 * MOV/HEIC/HEIF files in desktop pickers that cannot map their extension to a MIME type, so the
 * supported extensions are listed explicitly, built from the tables above.
 */
export const UPLOAD_FILE_ACCEPT = [
  'image/*',
  'video/*',
  ...Object.keys(VIDEO_MIME_TYPES).map((extension) => `.${extension}`),
  ...Object.keys(IMAGE_MIME_TYPES).map((extension) => `.${extension}`),
].join(',');

const GENERIC_BINARY_MIME_TYPE = 'application/octet-stream';

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const UNSUPPORTED = Object.freeze({ kind: null, mimeType: null });

const getLowerCaseExtension = (fileName) => {
  const name = typeof fileName === 'string' ? fileName : '';
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex === -1) {
    return '';
  }
  return name.slice(dotIndex + 1).toLowerCase();
};

/**
 * Mirrors the API's `isSupportedOriginalUpload` and `uploadMediaKind` (both run the case table in
 * wedding-functions/test/support/upload-type-cases.js): the extension must be a known one, a video
 * extension is video, and an image extension is an image only with an `image/` or no useful type.
 *
 * The PWA is a little stricter than the API where the browser reports a media type that
 * contradicts the extension (an `.mp4` typed `image/jpeg`, a `.mov` typed `text/plain`): "any
 * other reported type is a real signal that this is not photo or video media", so it is rejected
 * rather than sent. The browser's own `video/` or `image/` spelling is kept as the MIME type;
 * the API answers with the type it signs for the upload.
 *
 * @param {{ name?: string, type?: string } | null | undefined} file a File (or File-like object)
 * @returns {{ kind: 'video' | 'image' | null, mimeType: string | null }} `kind` is null (and
 *   `mimeType` null) when the file is not a supported photo or video.
 */
export function getUploadFileType(file) {
  if (!file) {
    return UNSUPPORTED;
  }

  const browserType = typeof file.type === 'string' ? file.type : '';
  const normalizedType = browserType.toLowerCase();
  const isBlankOrGeneric = normalizedType === '' || normalizedType === GENERIC_BINARY_MIME_TYPE;
  const extension = getLowerCaseExtension(file.name);

  if (hasOwn(VIDEO_MIME_TYPES, extension)) {
    const tableType = VIDEO_MIME_TYPES[extension];
    if (normalizedType.startsWith('video/')) {
      return { kind: 'video', mimeType: browserType };
    }
    // application/mxf is the one video type that does not start with `video/`; a browser that
    // reports exactly the table's type for the extension agrees with it.
    if (isBlankOrGeneric || normalizedType === tableType) {
      return { kind: 'video', mimeType: tableType };
    }
    return UNSUPPORTED;
  }

  if (hasOwn(IMAGE_MIME_TYPES, extension)) {
    if (normalizedType.startsWith('image/')) {
      return { kind: 'image', mimeType: browserType };
    }
    if (isBlankOrGeneric) {
      return { kind: 'image', mimeType: IMAGE_MIME_TYPES[extension] };
    }
  }

  return UNSUPPORTED;
}
